import { Injectable, NgZone } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { BehaviorSubject, filter, map, Observable, race, skip, take } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PRODUCT_KEY } from '../constants/general';
import 'cordova-plugin-purchase';
import { CacheService } from './cache.service';
import { UserService } from './user.service';
import { AnalyticsService } from './analytics.service';

// https://devdactic.com/ionic-in-app-purchase-capacitor
@Injectable({
  providedIn: 'root',
})
export class PurchaseService {
  public weeklyScanProduct: CdvPurchase.Product | null = null;

  private store!: CdvPurchase.Store;

  constructor(
    private ngZone: NgZone,
    private cacheService: CacheService,
    private userService: UserService,
    private analyticsService: AnalyticsService
  ) {}

  public get lastRenewalDate$(): Observable<Date | null> {
    return this.userService.purchaseData$.pipe(
      map(purchaseData => {
        return purchaseData ? new Date(purchaseData.lastRenewalDate) : null;
      })
    );
  }

  public get expirationDate$(): Observable<Date | null> {
    return this.userService.purchaseData$.pipe(
      map(purchaseData => {
        return purchaseData ? new Date(purchaseData.expirationDate) : null;
      })
    );
  }

  public get lastRenewalDate(): Date | null {
    return this.userService.purchaseData ? new Date(this.userService.purchaseData.lastRenewalDate) : null;
  }

  public get expirationDate(): Date | null {
    return this.userService.purchaseData ? new Date(this.userService.purchaseData.expirationDate) : null;
  }

  public get platform() {
    return Capacitor.getPlatform() === 'ios'
      ? CdvPurchase.Platform.APPLE_APPSTORE
      : CdvPurchase.Platform.GOOGLE_PLAY;
  }

  public async init() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    // must wait for capacitor to be ready
    this.store = CdvPurchase.store;

    console.log('IAP Store initialized');

    if (environment.production) {
      console.log('IAP Store is in production mode');
    } else {
      console.log('IAP Store is in development mode');
      CdvPurchase.store.verbosity = CdvPurchase.LogLevel.DEBUG;
    }

    this.store.validator = `https://validator.iaptic.com/v3/validate?appName=com.fitscanai.app&apiKey=ab6350d5-72dd-46e4-a0fc-2aaa6509d8ef`;
    this.store.applicationUsername = this.userService.uid || undefined;
    // Simulate a successful validation
    this.registerProducts();
    this.setupListeners();
    this.store.ready(() => {
      this.ngZone.run(() => {
        console.log('IAP Store is ready');
        console.log('products:', this.store.products);
        this.weeklyScanProduct = this.store.get(PRODUCT_KEY, this.platform) || null;
      });
    });

    await this.store.initialize([
      {
        platform: this.platform,
        options: {
          needAppReceipt: true,
        },
      },
    ]);
  }

  public manageSubscriptions() {
    console.log('Managing subscription...');
    this.store.manageSubscriptions();
  }

  public async purchase(productId: string) {
    console.log('Attempting to purchase product:', productId);
    const product = this.store.get(productId);
    if (!product) {
      return Promise.reject(new Error('Product not found'));
    }
    console.log('Product details:', product);
    const regularOffer = product?.offers?.find(offer => offer.id !== 'freescanweek') || null;
    if (!regularOffer) {
      return Promise.reject(new Error('No valid offer found'));
    }
    console.log('ordering regular offer:', regularOffer.id);
    const error = await regularOffer.order();
    if (error) {
      return Promise.reject(error);
    }
    console.log('Purchase initiated for product:', productId);
    this.analyticsService.logEvent('purchase');
  }

  public async restorePurchases() {
    const error = await this.store.restorePurchases();
    if (error) {
      return Promise.reject(error);
    }
    console.log('Purchases restored successfully');
  }

  private registerProducts() {
    console.log('Registering products with key:', PRODUCT_KEY);
    this.store.register({
      id: PRODUCT_KEY,
      platform:
        Capacitor.getPlatform() === 'ios'
          ? CdvPurchase.Platform.APPLE_APPSTORE
          : CdvPurchase.Platform.GOOGLE_PLAY,
      type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
    });
  }

  private setupListeners() {
    this.store
      .when()
      .productUpdated(p => {
        this.ngZone.run(() => {
          console.log('Product updated:', p);
          this.weeklyScanProduct = p;
        });
      })
      .approved(transaction => {
        this.ngZone.run(() => {
          console.log('Transaction approved:', transaction);
          transaction.verify();
        });
      })
      .verified(receipt => {
        this.ngZone.run(() => {
          console.log('Transaction verified:', receipt);
          receipt.finish();
        });
      })
      .finished(() => {
        this.ngZone.run(() => {
          console.log('Transaction finished successfully');
        });
      });
  }

  public get isSubscribed(): boolean {
    const purchaseData = this.userService.purchaseData;

    if (!purchaseData) {
      return false;
    }

    const periodStart = new Date(purchaseData.lastRenewalDate);
    const periodEnd = new Date(purchaseData.expirationDate);
    const now = new Date();
    const isActive = now >= periodStart && now <= periodEnd;
    // console.log('isSubscribed:', isActive, 'periodStart:', periodStart, 'periodEnd:', periodEnd, 'now:', now);
    return isActive;
  }
}
